import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { RESOLUTION, resolutionDescriptor } from '../../../Services/CustomerServiceDeskService';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * WHO HOLDS THIS CONVERSATION, AND THE ONE THING A PERSON MAY DO ABOUT IT.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS IS THE ANSWER TO "IT CREATES IT FOR EVERYONE … THEN MULTIPLE PEOPLE
 * COULD SEND IT." NOTHING HERE SENDS ANYTHING.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A shared mailbox's failure is two agents answering the same customer four
 * minutes apart, and a draft that is visible to everybody makes it likelier
 * rather than less. So a conversation has a holder, the holder is named on
 * the screen, and the controls offered depend on who that is:
 *
 *   NOBODY      → Take it.
 *   ME          → Hand it back, or close it.
 *   SOMEBODY    → nothing. No "take it anyway", deliberately: an override
 *                 button is the feature that makes the holder decorative, and
 *                 a conversation somebody is typing into is the exact thing
 *                 this is protecting. Releasing it is theirs to do.
 *   CLOSED      → nothing, and the sentence says why. The server refuses a
 *                 claim on a conversation already resolved FOR THE CURRENT
 *                 DRAFT — reopening it would discard the resolution — and a
 *                 NEW customer email reopens the thread by itself, because it
 *                 produces a new drafting run and the claim filter admits a
 *                 resolved row whose run differs. A "Reopen and take it"
 *                 button here could only ever raise a 409, which is worse
 *                 than no button and much worse than the fact.
 *
 * THE BROWSER DRAWS THE BUTTON; THE SERVER DECIDES. Which of these renders is
 * decided by comparing the holder with this session's email, and that
 * comparison is a rendering choice only — the server refuses a claim on a
 * held conversation with a 409 and the console repaints from the entry that
 * refusal carries. A browser that drew the wrong button would still be unable
 * to take the conversation.
 *
 * ── CLOSING IS TWO OUTCOMES HERE, AND "NOTHING WAS SENT" IS NOT THE LESSER ──
 * A newsletter, a supplier's receipt, a thank-you with nothing in it: the
 * right handling is to close it and send nothing. Both buttons are the same
 * size and the same colour for that reason. A desk that can only record
 * "replied" leaves every correctly-ignored conversation in the waiting count
 * for ever, which is how the one number this console exists to make
 * trustworthy stops being trusted.
 *
 * `wrote_own_reply` AND NOT A BARE "REPLIED", because that is what pressing
 * this button means: a person who had used the agent's draft would have
 * accepted it on the draft card, which records `used_draft`. The server keeps
 * the four apart precisely so "the draft stood" can be counted against "a
 * person wrote their own", and a button that reported either as one word would
 * make the comparison the owner asked for stop existing. The other two
 * resolutions belong to the draft card, which is where the draft is.
 */
function HolderControls({
  state, entry, busy, onClaim, onRelease, onResolve,
}) {
  const holder = entry?.holder;
  const resolution = entry?.resolution;
  const spinner = busy ? <Spinner animation="border" size="sm" className="me-2" /> : null;

  if (resolution) {
    // THE WORD IS THE SERVER'S OWN RESOLUTION, through the one descriptor
    // table. A sentence per outcome written here would be a second place that
    // decides what `used_draft` is called.
    const outcome = resolutionDescriptor(resolution.outcome);

    return (
      <div className="cs-holder cs-holder--resolved" data-testid="cs-holder">
        <span className="cs-holder__who">
          {`Closed — ${outcome?.label || resolution.outcome}`}
          {resolution.by ? ` · ${resolution.by}` : ''}
          {resolution.at ? ` · ${formatDateTime(resolution.at)}` : ''}
        </span>
        <span className="cs-holder__note">
          {resolution.outcome === RESOLUTION.REJECTED
            ? 'Nothing was sent. If this customer still needs an answer, answer'
              + ' them — their next email reopens this conversation here.'
            : 'A new email from this customer reopens it here.'}
        </span>
      </div>
    );
  }

  if (state.value === 'theirs') {
    return (
      <div className="cs-holder cs-holder--theirs" data-testid="cs-holder">
        <span className="cs-holder__who">
          <i className="fa fa-user-lock me-2" aria-hidden="true" />
          {/* THE EMAIL, because that is what the server knows. A claim's
              holder is the verified Cognito email and nothing on the record
              carries a display name for it; a `name` field here would have
              been a key nothing ever filled. */}
          {`${holder?.email} has this one`}
          {holder?.since ? ` · since ${formatDateTime(holder.since)}` : ''}
        </span>
        <span className="cs-holder__note">
          Nobody else can act on it until they hand it back.
        </span>
      </div>
    );
  }

  if (state.value === 'mine') {
    return (
      <div className="cs-holder cs-holder--mine" data-testid="cs-holder">
        <span className="cs-holder__who">
          <i className="fa fa-user-check me-2" aria-hidden="true" />
          You have this one
        </span>
        <div className="cs-holder__actions">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={busy}
            onClick={onRelease}
          >
            {spinner}
            Hand it back
          </Button>
          <Button
            size="sm"
            variant="outline-success"
            disabled={busy}
            onClick={() => onResolve(RESOLUTION.WROTE_OWN_REPLY)}
          >
            Close — I replied
          </Button>
          <Button
            size="sm"
            variant="outline-success"
            disabled={busy}
            onClick={() => onResolve(RESOLUTION.NO_REPLY_NEEDED)}
          >
            Close — no reply needed
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-holder" data-testid="cs-holder">
      <span className="cs-holder__who">Nobody has taken this.</span>
      <Button size="sm" variant="primary" disabled={busy} onClick={onClaim}>
        {spinner}
        Take it
      </Button>
    </div>
  );
}

export default HolderControls;
